import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Modal, Form, Badge, Pagination, Row, Col } from 'react-bootstrap';
import {
    fetchClientsRequest,
    createClientRequest,
    deleteClientRequest,
} from '../../store/actions/clientActions';
import { FiUsers, FiRefreshCw, FiPlus, FiEdit, FiTrash2, FiSearch, FiDownload, FiEye, FiCheckCircle, FiAlertTriangle, FiUpload } from 'react-icons/fi';
import { checkLcbftConformity } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminClient.css';

const EMPTY_CLIENT_FILTERS = {
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    lcbft_checked: '',
};

const CLIENTS_MAX_ROWS = 5;

const AdminClient = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { clients, create, update, delete: deleteState } = useSelector((state) => state.clients);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showLcbftModal, setShowLcbftModal] = useState(false);
    const [showLcbftConfirmModal, setShowLcbftConfirmModal] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [lcbftResult, setLcbftResult] = useState(null);
    const [clientToCheck, setClientToCheck] = useState(null);
    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        clearSearchState,
    } = usePersistedSearchState('clients', EMPTY_CLIENT_FILTERS, {
        defaultItemsPerPage: CLIENTS_MAX_ROWS,
    });
    const itemsPerPage = CLIENTS_MAX_ROWS;
    const [checkingLcbft, setCheckingLcbft] = useState({});
    const [checkError, setCheckError] = useState(null);
    const [checkSuccess, setCheckSuccess] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        client_address: '',
        city: '',
        zip_code: '',
        country: '',
        country_code: '',
        date_joined: '',
        role: 'Client',
        status: '',
        birth_date: '',
        place_of_birth: '',
        country_of_birth: '',
        title: null,
        iban: '',
        bic: '',
        account_holder_name: '',
    });

    useEffect(() => {
        const skip = (currentPage - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
            ...Object.fromEntries(
                Object.entries(appliedFilters).filter(([key, value]) => {
                    if (key === 'lcbft_checked') {
                        return value !== '' && value !== null && value !== undefined;
                    }
                    return value && value.trim() !== '';
                })
            ),
        };
        // Convertir lcbft_checked en booléen si présent
        if (params.lcbft_checked !== undefined) {
            if (params.lcbft_checked === 'true' || params.lcbft_checked === true) {
                params.lcbft_checked = true;
            } else if (params.lcbft_checked === 'false' || params.lcbft_checked === false) {
                params.lcbft_checked = false;
            } else {
                delete params.lcbft_checked;
            }
        }
        dispatch(fetchClientsRequest(params));
    }, [dispatch, currentPage, itemsPerPage, appliedFilters]);

    useEffect(() => {
        if (create.success || update.success || deleteState.success) {
            setShowCreateModal(false);
            setShowDeleteModal(false);
            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                client_address: '',
                city: '',
                zip_code: '',
                country: '',
                country_code: '',
                date_joined: '',
                role: 'Client',
                status: '',
                birth_date: '',
                place_of_birth: '',
                country_of_birth: '',
                title: null,
                iban: '',
                bic: '',
                account_holder_name: '',
            });
            const skip = (currentPage - 1) * itemsPerPage;
            const params = {
                skip,
                limit: itemsPerPage,
                ...Object.fromEntries(
                    Object.entries(appliedFilters).filter(([key, value]) => {
                        if (key === 'lcbft_checked') {
                            return value !== '' && value !== null && value !== undefined;
                        }
                        return value && value.trim() !== '';
                    })
                ),
            };
            // Convertir lcbft_checked en booléen si présent
            if (params.lcbft_checked !== undefined) {
                if (params.lcbft_checked === 'true' || params.lcbft_checked === true) {
                    params.lcbft_checked = true;
                } else if (params.lcbft_checked === 'false' || params.lcbft_checked === false) {
                    params.lcbft_checked = false;
                } else {
                    delete params.lcbft_checked;
                }
            }
            dispatch(fetchClientsRequest(params));
        }
    }, [create.success, update.success, deleteState.success, dispatch, currentPage, itemsPerPage, appliedFilters]);

    const handleRefresh = () => {
        const skip = (currentPage - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
            ...Object.fromEntries(
                Object.entries(appliedFilters).filter(([key, value]) => {
                    if (key === 'lcbft_checked') {
                        return value !== '' && value !== null && value !== undefined;
                    }
                    return value && value.trim() !== '';
                })
            ),
        };
        // Convertir lcbft_checked en booléen si présent
        if (params.lcbft_checked !== undefined) {
            if (params.lcbft_checked === 'true' || params.lcbft_checked === true) {
                params.lcbft_checked = true;
            } else if (params.lcbft_checked === 'false' || params.lcbft_checked === false) {
                params.lcbft_checked = false;
            } else {
                delete params.lcbft_checked;
            }
        }
        dispatch(fetchClientsRequest(params));
    };

    const handleCreate = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            client_address: '',
            city: '',
            zip_code: '',
            country: '',
            country_code: '',
            date_joined: '',
            role: 'Client',
            status: '',
            birth_date: '',
            place_of_birth: '',
            country_of_birth: '',
            title: null,
            iban: '',
            bic: '',
            account_holder_name: '',
        });
        setShowCreateModal(true);
    };

    const handleEdit = (clientId) => {
        if (clientId) {
            navigate(`/admin/client/edit/${clientId}`);
        }
    };

    const handleDetails = (clientId) => {
        if (clientId) {
            navigate(`/admin/client/${clientId}`);
        }
    };

    const handleDelete = (clientId) => {
        setSelectedClientId(clientId);
        setShowDeleteModal(true);
    };

    const handleSubmitCreate = (e) => {
        e.preventDefault();
        dispatch(createClientRequest(formData));
    };

    const handleConfirmDelete = () => {
        dispatch(deleteClientRequest(selectedClientId));
    };

    const handleCheckLcbft = async (clientId) => {
        try {
            setCheckError(null);
            setCheckSuccess(null);
            setCheckingLcbft((prev) => ({ ...prev, [clientId]: true }));

            // Trouver le client pour afficher ses informations dans le modal
            const client = clientsArray.find(
                (c) => (c.client_id || c.id || c.user_uuid) === clientId
            );

            try {
                const result = await checkLcbftConformity(clientId);

                // Succès - ouvrir le modal avec le résultat
                setLcbftResult({
                    success: true,
                    clientId: clientId,
                    client: client,
                    message: result?.message || 'Vérification LCBFT réussie',
                    data: result
                });
                setShowLcbftModal(true);
                setCheckingLcbft((prev) => ({ ...prev, [clientId]: false }));

                // Rafraîchir les données après la vérification
                const skip = (currentPage - 1) * itemsPerPage;
                const params = {
                    skip,
                    limit: itemsPerPage,
                    ...Object.fromEntries(
                        Object.entries(appliedFilters).filter(([key, value]) => {
                            if (key === 'lcbft_checked') {
                                return value !== '' && value !== null && value !== undefined;
                            }
                            return value && value.trim() !== '';
                        })
                    ),
                };
                // Convertir lcbft_checked en booléen si présent
                if (params.lcbft_checked !== undefined) {
                    if (params.lcbft_checked === 'true' || params.lcbft_checked === true) {
                        params.lcbft_checked = true;
                    } else if (params.lcbft_checked === 'false' || params.lcbft_checked === false) {
                        params.lcbft_checked = false;
                    } else {
                        delete params.lcbft_checked;
                    }
                }
                dispatch(fetchClientsRequest(params));
            } catch (error) {
                // Erreur - extraire le message détaillé
                let errorDetail = 'Erreur lors de la vérification LCBFT';
                let errorMessage = '';

                // Essayer d'extraire le message d'erreur de différentes sources
                if (error.response?.data) {
                    const errorData = error.response.data;

                    // Priorité 1: detail (message principal)
                    if (errorData.detail) {
                        errorDetail = errorData.detail;
                    }
                    // Priorité 2: message
                    else if (errorData.message) {
                        errorDetail = errorData.message;
                    }
                    // Priorité 3: error
                    else if (errorData.error) {
                        errorDetail = errorData.error;
                    }
                } else if (error.message) {
                    errorDetail = error.message;
                }

                // Formater le message d'erreur
                if (typeof errorDetail === 'string') {
                    errorMessage = errorDetail;
                } else if (errorDetail && typeof errorDetail === 'object') {
                    errorMessage = JSON.stringify(errorDetail, null, 2);
                } else {
                    errorMessage = String(errorDetail);
                }

                // Ouvrir le modal avec l'erreur
                setLcbftResult({
                    success: false,
                    clientId: clientId,
                    client: client,
                    message: errorMessage,
                    error: error.response?.data || { detail: errorMessage }
                });
                setShowLcbftModal(true);
                setCheckingLcbft((prev) => ({ ...prev, [clientId]: false }));
            }
        } catch (error) {
            // Erreur générale
            const errorMessage = handleApiError(error, 'Erreur lors de la vérification LCBFT');
            setCheckError(errorMessage);
            setCheckingLcbft((prev) => ({ ...prev, [clientId]: false }));

            // Effacer le message d'erreur après 5 secondes
            setTimeout(() => {
                setCheckError(null);
            }, 5000);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? e.target.checked : (value === '' && name === 'title' ? null : value),
        }));
    };

    // Ensure clients.data is an array
    const clientsArray = useMemo(() => {
        const raw = clients?.data;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw?.clients)) return raw.clients;
        if (Array.isArray(raw?.data)) return raw.data;
        if (Array.isArray(raw?.items)) return raw.items;
        if (Array.isArray(raw?.results)) return raw.results;
        if (raw?.client) return [raw.client];
        if (raw) return [raw];
        return [];
    }, [clients.data]);

    const meta = useMemo(() => clients?.data?.meta || {
        total: clientsArray.length,
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: Math.max(1, Math.ceil(clientsArray.length / itemsPerPage)),
    }, [clients?.data?.meta, clientsArray.length, currentPage, itemsPerPage]);

    const totalPages = meta.total_pages || Math.max(1, Math.ceil((meta.total || 0) / itemsPerPage));
    const totalItems = meta.total || clientsArray.length;
    const currentClients = clientsArray;

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const sanitized = Object.fromEntries(
            Object.entries(filters).map(([key, value]) => {
                if (key === 'lcbft_checked') {
                    return [key, value];
                }
                return [key, value.trim()];
            })
        );
        setAppliedFilters(sanitized);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        clearSearchState();
    };

    const handleFilterKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleApplyFilters();
        }
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return;
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <div className="dashboard-page admin-client-page">
            {(create.error || update.error || deleteState.error) && (
                <Alert variant="danger" dismissible>
                    {create.error || update.error || deleteState.error}
                </Alert>
            )}

            {(create.success || update.success || deleteState.success) && (
                <Alert variant="success" dismissible>
                    Opération réussie !
                </Alert>
            )}

            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiUsers className="me-2" />
                                Gestion des Clients
                            </h2>
                            <p className="text-muted mb-0">Gérez tous les clients assurés</p>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button
                                variant="outline-warning"
                                onClick={() => navigate('/admin/client/import-phone')}
                                className="d-flex align-items-center"
                            >
                                <FiUpload className="me-2" /> Import modifier client
                            </Button>
                            <Button variant="outline-primary" onClick={handleRefresh} disabled={clients.loading}>
                                <FiRefreshCw className={clients.loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                            <Button variant="primary" onClick={handleCreate}>
                                <FiPlus /> Nouveau Client
                            </Button>
                        </div>
                    </div>

                    {clients.error && (
                        <Alert variant="danger" className="mt-3">
                            {clients.error}
                        </Alert>
                    )}

                    {checkError && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => setCheckError(null)}>
                            {checkError}
                        </Alert>
                    )}

                    {checkSuccess && (
                        <Alert variant="success" className="mt-3" dismissible onClose={() => setCheckSuccess(null)}>
                            {checkSuccess}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleFilterKeyPress}
                                            placeholder="exemple@client.com"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Téléphone</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="phone_number"
                                            value={filters.phone_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleFilterKeyPress}
                                            placeholder="+33600000000"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="first_name"
                                            value={filters.first_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleFilterKeyPress}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="last_name"
                                            value={filters.last_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleFilterKeyPress}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>LCBFT vérifié</Form.Label>
                                        <Form.Select
                                            name="lcbft_checked"
                                            value={filters.lcbft_checked}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Tous</option>
                                            <option value="true">Oui</option>
                                            <option value="false">Non</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex align-items-center gap-3 mt-3">
                                <span className="text-muted ms-auto">
                                    Page {currentPage} sur {totalPages} — Total : {totalItems}
                                </span>
                            </div>
                        </Card.Body>
                    </Card>

                    {clients.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des clients...</p>
                        </div>
                    ) : currentClients.length === 0 ? (
                        <div className="text-center py-5">
                            <FiUsers size={48} className="text-muted mb-3" />
                            <p className="text-muted">
                                {appliedFilters.email || appliedFilters.first_name || appliedFilters.last_name || appliedFilters.phone_number || appliedFilters.lcbft_checked
                                    ? 'Aucun client trouvé avec ces filtres'
                                    : 'Aucun client disponible'}
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive table-scroll">
                            <Table hover striped className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Nom</th>
                                        <th>Prénom</th>
                                        <th>Email</th>
                                        <th>IBAN reçu</th>
                                        <th>LCBFT</th>
                                        <th className="text-end col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(currentClients) && currentClients.length > 0 ? (
                                        currentClients.map((client) => {
                                            const clientId = client.id || client.client_id || client.user_uuid;
                                            return (
                                                <tr key={clientId}>
                                                    <td>{client.last_name || 'N/A'}</td>
                                                    <td>{client.first_name || 'N/A'}</td>
                                                    <td>{client.email || 'N/A'}</td>
                                                    <td>
                                                        <Badge bg={client.is_iban_received ? 'success' : 'warning'}>
                                                            {client.is_iban_received ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={client.lcbft_checked ? 'success' : 'secondary'}>
                                                            {client.lcbft_checked ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end col-actions">
                                                        <div className="d-flex gap-2 justify-content-end flex-nowrap">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleDetails(clientId)}
                                                                title="Détails"
                                                                aria-label="Détails"
                                                            >
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleEdit(clientId)}
                                                                title="Modifier"
                                                                aria-label="Modifier"
                                                            >
                                                                <FiEdit />
                                                            </Button>
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const client = currentClients.find(
                                                                        (c) => (c.client_id || c.id || c.user_uuid) === clientId
                                                                    );
                                                                    setClientToCheck({ id: clientId, client });
                                                                    setShowLcbftConfirmModal(true);
                                                                }}
                                                                title="Vérifier LCBFT"
                                                                aria-label="Vérifier LCBFT"
                                                                disabled={checkingLcbft[clientId]}
                                                            >
                                                                {checkingLcbft[clientId] ? (
                                                                    <Spinner animation="border" size="sm" />
                                                                ) : (
                                                                    <FiCheckCircle />
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(clientId)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                            >
                                                                <FiTrash2 />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center">
                                                Aucun client trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!clients.loading && totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                />
                                {renderPaginationItems()}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                />
                                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Create Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Nouveau Client</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitCreate}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nom *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Téléphone</Form.Label>
                            <Form.Control
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Adresse</Form.Label>
                            <Form.Control
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <div className="row">
                            <Form.Group className="col-md-6 mb-3">
                                <Form.Label>Ville</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-3 mb-3">
                                <Form.Label>Code Postal</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="zip_code"
                                    value={formData.zip_code}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-3 mb-3">
                                <Form.Label>Pays</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                            Annuler
                        </Button>
                        <Button variant="primary" type="submit" disabled={create.loading}>
                            {create.loading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Création...
                                </>
                            ) : (
                                'Créer'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteState.loading}>
                        {deleteState.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Supprimer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* LCBFT Confirmation Modal */}
            <Modal show={showLcbftConfirmModal} onHide={() => setShowLcbftConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Confirmer la vérification LCBFT
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {clientToCheck && clientToCheck.client && (
                        <div>
                            <p>Êtes-vous sûr de vouloir vérifier le LCBFT pour ce client ?</p>
                            <div className="mt-3">
                                <Row>
                                    <Col md={6}>
                                        <strong>Nom :</strong> {clientToCheck.client.last_name || 'N/A'}
                                    </Col>
                                    <Col md={6}>
                                        <strong>Prénom :</strong> {clientToCheck.client.first_name || 'N/A'}
                                    </Col>
                                    <Col md={6} className="mt-2">
                                        <strong>Email :</strong> {clientToCheck.client.email || 'N/A'}
                                    </Col>
                                    <Col md={6} className="mt-2">
                                        <strong>Date de naissance :</strong> {clientToCheck.client.birth_date || 'N/A'}
                                    </Col>
                                    <Col md={6} className="mt-2">
                                        <strong>Code client :</strong> <code>{clientToCheck.client.client_code || 'N/A'}</code>
                                    </Col>
                                </Row>
                            </div>
                            <Alert variant="info" className="mt-3">
                                <FiAlertTriangle className="me-2" />
                                Cette action va déclencher une vérification LCBFT pour ce client. Le processus peut prendre quelques instants.
                            </Alert>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowLcbftConfirmModal(false)}>
                        Annuler
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => {
                            if (clientToCheck && clientToCheck.id) {
                                setShowLcbftConfirmModal(false);
                                handleCheckLcbft(clientToCheck.id);
                            }
                        }}
                        disabled={!clientToCheck || checkingLcbft[clientToCheck?.id]}
                    >
                        {checkingLcbft[clientToCheck?.id] ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Vérification en cours...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle className="me-2" />
                                Confirmer la vérification
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* LCBFT Check Modal */}
            <Modal show={showLcbftModal} onHide={() => setShowLcbftModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Vérification LCBFT
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {lcbftResult && (
                        <>
                            {/* Informations du client */}
                            {lcbftResult.client && (
                                <div className="mb-4">
                                    <h6 className="mb-3">Informations du client</h6>
                                    <Row>
                                        <Col md={6}>
                                            <strong>Nom :</strong> {lcbftResult.client.last_name || 'N/A'}
                                        </Col>
                                        <Col md={6}>
                                            <strong>Prénom :</strong> {lcbftResult.client.first_name || 'N/A'}
                                        </Col>
                                        <Col md={6} className="mt-2">
                                            <strong>Email :</strong> {lcbftResult.client.email || 'N/A'}
                                        </Col>
                                        <Col md={6} className="mt-2">
                                            <strong>Date de naissance :</strong> {lcbftResult.client.birth_date || 'N/A'}
                                        </Col>
                                        <Col md={6} className="mt-2">
                                            <strong>ID Client :</strong> <code>{lcbftResult.clientId}</code>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Résultat de la vérification */}
                            {lcbftResult.success ? (
                                <Alert variant="success">
                                    <FiCheckCircle className="me-2" />
                                    <strong>Vérification réussie</strong>
                                    <p className="mb-0 mt-2">{lcbftResult.message}</p>
                                    {lcbftResult.data && (
                                        <div className="mt-3">
                                            <pre style={{
                                                backgroundColor: '#f8f9fa',
                                                padding: '10px',
                                                borderRadius: '4px',
                                                fontSize: '0.9rem',
                                                overflow: 'auto'
                                            }}>
                                                {JSON.stringify(lcbftResult.data, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </Alert>
                            ) : (
                                <Alert variant="danger">
                                    <FiAlertTriangle className="me-2" />
                                    <strong>Erreur de vérification</strong>
                                    <div className="mt-3">
                                        <p className="mb-2"><strong>Message d'erreur :</strong></p>
                                        <div style={{
                                            backgroundColor: '#f8d7da',
                                            padding: '12px',
                                            borderRadius: '4px',
                                            border: '1px solid #f5c6cb',
                                            fontSize: '0.9rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}>
                                            {lcbftResult.message || 'Une erreur est survenue lors de la vérification LCBFT'}
                                        </div>
                                    </div>
                                    {lcbftResult.error && typeof lcbftResult.error === 'object' && (
                                        <div className="mt-3">
                                            <p className="mb-2"><strong>Détails de l'erreur :</strong></p>
                                            <pre style={{
                                                backgroundColor: '#f8f9fa',
                                                padding: '10px',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                overflow: 'auto',
                                                maxHeight: '200px'
                                            }}>
                                                {JSON.stringify(lcbftResult.error, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowLcbftModal(false)}>
                        Fermer
                    </Button>
                    {lcbftResult && !lcbftResult.success && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowLcbftModal(false);
                                // Rafraîchir les données
                                handleRefresh();
                            }}
                        >
                            Actualiser
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminClient;

